import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import StockInHand from "./pages/StockInHand";
import PurchaseOrders from "./pages/PurchaseOrders";
import PurchaseOrderPrint from "./pages/PurchaseOrderPrint";
import FabricStock from "./pages/FabricStock";
import ProductionOrders from "./pages/ProductionOrders";
import ProductionStage from "./pages/ProductionStage";
import ProductionOrderDetail from "./pages/ProductionOrderDetail";
import Cutting from "./pages/Cutting";
import ProcessIssue from "./pages/ProcessIssue";
import Challans from "./pages/Challans";
import ChallanPrint from "./pages/ChallanPrint";
import ProcessReceiving from "./pages/ProcessReceiving";
import FabricDocs from "./pages/FabricDocs";
import ClearedSlips from "./pages/ClearedSlips";
import FabricDocPrint from "./pages/FabricDocPrint";
import Wip from "./pages/Wip";
import FinishedGoods from "./pages/FinishedGoods";
import Accessories from "./pages/Accessories";
import SalesOrders from "./pages/SalesOrders";
import Jobbers from "./pages/Jobbers";
import { Suppliers, Customers } from "./pages/Parties";
import ProcessMaster from "./pages/ProcessMaster";
import JobberBills from "./pages/JobberBills";
import Reports from "./pages/Reports";
import Audit from "./pages/Audit";
import SettingsPage from "./pages/SettingsPage";

function PendingScreen({ user }) {
  const { logout } = useAuth();
  const status = user?.status;
  const msg =
    status === "rejected" ? "Your account request was rejected. Please contact the administrator."
      : status === "disabled" ? "Your account has been disabled. Please contact the administrator."
        : "Your account is pending administrator approval. Please contact the administrator.";
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
      <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <div className="h-14 w-14 rounded-full bg-amber-100 text-amber-600 grid place-items-center mx-auto mb-4 text-2xl font-bold">!</div>
        <h1 className="text-lg font-semibold text-slate-800 mb-2">Access {status === "rejected" ? "Denied" : status === "disabled" ? "Disabled" : "Pending"}</h1>
        <p className="text-sm text-slate-500 mb-6" data-testid="pending-message">{msg}</p>
        <button onClick={logout} className="text-sm text-blue-700 hover:underline" data-testid="pending-logout">Sign out</button>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/" replace />;
  if (user.status && user.status !== "active") return <PendingScreen user={user} />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/stock-in-hand" element={<Protected><StockInHand /></Protected>} />
      <Route path="/purchase-orders" element={<Protected><PurchaseOrders /></Protected>} />
      <Route path="/purchase-orders/:id/print" element={<Protected><PurchaseOrderPrint /></Protected>} />
      <Route path="/fabric-stock" element={<Protected><FabricStock /></Protected>} />
      <Route path="/fabric-docs" element={<Protected><FabricDocs /></Protected>} />
      <Route path="/fabric-docs/:kind/:id" element={<Protected><FabricDocPrint /></Protected>} />
      <Route path="/production-orders" element={<Protected><ProductionOrders /></Protected>} />
      <Route path="/production-stage" element={<Protected><ProductionStage /></Protected>} />
      <Route path="/production-orders/:id" element={<Protected><ProductionOrderDetail /></Protected>} />
      <Route path="/cutting" element={<Protected><Cutting /></Protected>} />
      <Route path="/process-issue" element={<Protected><ProcessIssue /></Protected>} />
      <Route path="/challans" element={<Protected><Challans /></Protected>} />
      <Route path="/challans/*" element={<Protected><ChallanPrint /></Protected>} />
      <Route path="/receiving" element={<Protected><ProcessReceiving /></Protected>} />
      <Route path="/wip" element={<Protected><Wip /></Protected>} />
      <Route path="/cleared-slips" element={<Protected><ClearedSlips /></Protected>} />
      <Route path="/finished-goods" element={<Protected><FinishedGoods /></Protected>} />
      <Route path="/accessories" element={<Protected><Accessories /></Protected>} />
      <Route path="/sales-orders" element={<Protected><SalesOrders /></Protected>} />
      <Route path="/jobbers" element={<Protected><Jobbers /></Protected>} />
      <Route path="/suppliers" element={<Protected><Suppliers /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/process-master" element={<Protected><ProcessMaster /></Protected>} />
      <Route path="/jobber-bills" element={<Protected><JobberBills /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
